import * as paymentApi from '@/api/payment';
import type { PaymentRecord } from '@/api/payment';
import { Modal, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PaymentHistoryDialogProps {
    visible: boolean;
    onClose: () => void;
}

const statusColorMap: Record<string, string> = {
    PENDING: 'gold',
    SUCCEEDED: 'green',
    FAILED: 'red',
    CANCELLED: 'default',
};

const PaymentHistoryDialog: React.FC<PaymentHistoryDialogProps> = ({
    visible,
    onClose,
}) => {
    const { t } = useTranslation('common');
    const [loading, setLoading] = useState(false);
    const [payments, setPayments] = useState<PaymentRecord[]>([]);

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await paymentApi.getMyPayments();
            if (res?.ok) {
                setPayments(res.data ?? []);
            } else {
                message.error(res?.message ?? t('payment_history.load_failed'));
            }
        } catch {
            message.error(t('payment_history.load_failed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (visible) {
            fetchPayments();
        } else {
            setPayments([]);
        }
    }, [visible, fetchPayments]);

    const columns: ColumnsType<PaymentRecord> = [
        {
            title: t('payment_history.col_order_id'),
            dataIndex: 'merchant_order_id',
            key: 'merchant_order_id',
            ellipsis: true,
        },
        {
            title: t('payment_history.col_amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number, record: PaymentRecord) =>
                `${record.currency} ${amount.toFixed(2)}`,
        },
        {
            title: t('payment_history.col_status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={statusColorMap[status] ?? 'default'}>{status}</Tag>
            ),
        },
        {
            title: t('payment_history.col_created_at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (val: string) => dayjs(val).format('YYYY-MM-DD HH:mm:ss'),
        },
    ];

    return (
        <Modal
            title={t('payment_history.title')}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={720}
            destroyOnClose
        >
            <Table<PaymentRecord>
                columns={columns}
                dataSource={payments}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                size="small"
            />
        </Modal>
    );
};

export default PaymentHistoryDialog;
